import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { authHeaders, Toast, getODataRows } from './listPageStyles';
import { StatusBadge, RelationPicker } from './ui';
import { RELATION_CONFIGS } from './relationConfigs';
import { usePageMeta } from '../hooks/usePageMeta';
import {
  DetailShell, Section, Grid, Icon, SubModal, SplitButton,
  FieldText, FieldSelect, FieldDate, FieldTextarea, FieldCustom,
} from './detail/DetailShell';
import { usePageActions } from '../contexts/PermissionContext';

const API_BASE = '/api/agreements';
const CUSTOMERS_API = '/api/customers';

const TYPE_OPTIONS = [
  { value: 'DSA', label: 'DSA' },
  { value: 'SPACEMAN', label: 'Spaceman' },
  { value: 'CONTRACT', label: 'Sözleşme' },
];
const PERIOD_OPTIONS = [
  { value: 'ANNUAL', label: 'Yıllık' },
  { value: 'PERIODIC', label: 'Periyodik' },
  { value: 'MONTHLY', label: 'Aylık' },
];
const STATUS_OPTIONS = [
  { value: 'ACTIVE', label: 'Aktif' },
  { value: 'PENDING', label: 'Beklemede' },
  { value: 'EXPIRED', label: 'Süresi Dolmuş' },
];
const STATUS_META = {
  ACTIVE: { label: 'Aktif', tone: 'ok' },
  PENDING: { label: 'Beklemede', tone: 'wait' },
  EXPIRED: { label: 'Süresi Doldu', tone: 'bad' },
};

const EMPTY_FORM = {
  customerId: '',
  customerTitle: '',
  title: '',
  type: 'CONTRACT',
  startDate: '',
  endDate: '',
  period: 'ANNUAL',
  content: '',
  attachedPdfUrl: '',
  autoDiscountPct: '',
  status: 'ACTIVE',
  signedAt: '',
};

function pick(obj, ...keys) {
  if (!obj) return undefined;
  for (const k of keys) if (obj[k] !== undefined && obj[k] !== null) return obj[k];
  return undefined;
}
function unwrap(payload) { return pick(payload, 'Data', 'data') ?? payload; }

function customerLookupLabel(item) {
  if (!item) return '';
  const code = item.textId || item.TextId || '';
  const desc = item.title || item.Title || item.name || item.Name || '';
  return desc ? (code ? `${code} - ${desc}` : desc) : code;
}

export function AgreementDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isNew = !id || id === 'new';
  const { canAdd, canEdit, canDelete, canCopy } = usePageActions();
  const canSave = isNew ? canAdd : canEdit;
  usePageMeta(isNew ? 'Yeni Anlaşma' : 'Anlaşma Detayı', 'DSA / Spaceman / yıllık sözleşme master kaydı.');

  const [form, setForm] = useState(EMPTY_FORM);
  const [audit, setAudit] = useState(null);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [tried, setTried] = useState(false);
  const [toast, setToast] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(false);

  const showToast = (type, message) => { setToast({ type, message }); setTimeout(() => setToast(null), 3500); };
  const set = (k, v) => setForm((c) => ({ ...c, [k]: v }));

  // === Müşteri lookup (4000+ — tümü yüklenmez, lazy etiket) ===
  const fetchedCustomerRef = useRef(new Set());
  const ensureCustomerLabel = useCallback(async (custId) => {
    if (!custId || fetchedCustomerRef.current.has(custId)) return;
    fetchedCustomerRef.current.add(custId);
    try {
      const url = `${CUSTOMERS_API}?$top=1&$filter=${encodeURIComponent(`Id eq ${custId}`)}`;
      const res = await fetch(url, { headers: authHeaders() });
      if (!res.ok) return;
      const data = await res.json();
      const c = getODataRows(data)[0];
      if (c) setForm((curr) => (curr.customerId === custId && !curr.customerTitle ? { ...curr, customerTitle: customerLookupLabel(c) } : curr));
    } catch { /* sessiz */ }
  }, []);

  // Tek kayıt yükleme: önce location.state.row (liste detay içermez → content boş), sonra GetById ile tam kayıt.
  const load = useCallback(async () => {
    if (isNew) { setLoading(false); return; }
    setLoading(true);
    // Önce listeden gelen satırla hızlı doldur (varsa).
    const seed = location.state?.row;
    if (seed) {
      setForm((curr) => ({
        ...curr,
        customerId: pick(seed, 'customerId', 'CustomerId') || '',
        customerTitle: pick(seed, 'customerTitle', 'CustomerTitle') || '',
        title: pick(seed, 'title', 'Title') || '',
        type: pick(seed, 'type', 'Type') || 'CONTRACT',
        startDate: pick(seed, 'startDate', 'StartDate') || '',
        endDate: pick(seed, 'endDate', 'EndDate') || '',
        period: pick(seed, 'period', 'Period') || 'ANNUAL',
        attachedPdfUrl: pick(seed, 'attachedPdfUrl', 'AttachedPdfUrl') || '',
        autoDiscountPct: pick(seed, 'autoDiscountPct', 'AutoDiscountPct') ?? '',
        status: pick(seed, 'status', 'Status') || 'ACTIVE',
        signedAt: pick(seed, 'signedAt', 'SignedAt') || '',
      }));
    }
    try {
      const res = await fetch(`${API_BASE}/${id}`, { headers: authHeaders() });
      if (!res.ok) throw new Error(`Anlaşma alınamadı (${res.status})`);
      const r = unwrap(await res.json());
      setForm({
        customerId: pick(r, 'customerId', 'CustomerId') || '',
        customerTitle: pick(r, 'customerTitle', 'CustomerTitle') || (seed ? pick(seed, 'customerTitle', 'CustomerTitle') : '') || '',
        title: pick(r, 'title', 'Title') || '',
        type: pick(r, 'type', 'Type') || 'CONTRACT',
        startDate: pick(r, 'startDate', 'StartDate') || '',
        endDate: pick(r, 'endDate', 'EndDate') || '',
        period: pick(r, 'period', 'Period') || 'ANNUAL',
        content: pick(r, 'content', 'Content') || '',
        attachedPdfUrl: pick(r, 'attachedPdfUrl', 'AttachedPdfUrl') || '',
        autoDiscountPct: pick(r, 'autoDiscountPct', 'AutoDiscountPct') ?? '',
        status: pick(r, 'status', 'Status') || 'ACTIVE',
        signedAt: pick(r, 'signedAt', 'SignedAt') || '',
      });
      setAudit({ createdBy: pick(r, 'createdBy', 'CreatedBy', 'createdUser', 'CreatedUser'), createdDate: pick(r, 'createdDate', 'CreatedDate'), updatedBy: pick(r, 'updatedBy', 'UpdatedBy', 'modifiedUser', 'ModifiedUser'), updatedDate: pick(r, 'updatedDate', 'UpdatedDate', 'modifiedDate', 'ModifiedDate') });
    } catch (err) {
      showToast('error', err.message || 'Anlaşma alınamadı');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isNew]);

  useEffect(() => { load(); }, [load]);

  // Müşteri etiketi yoksa lazy çek (örn. liste/detay etiket döndürmezse).
  useEffect(() => {
    if (!isNew && form.customerId && !form.customerTitle) ensureCustomerLabel(form.customerId);
  }, [isNew, form.customerId, form.customerTitle, ensureCustomerLabel]);

  // Klonla: /agreements/new'e kopya veriyle gelindiyse formu doldur (kullanıcı Kaydet diyene kadar kayıt OLUŞMAZ).
  useEffect(() => {
    if (isNew && location.state?.clone) {
      setForm(location.state.clone);
      setTried(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  const buildBody = () => ({
    customerId: form.customerId.trim(),
    title: form.title.trim(),
    type: form.type,
    startDate: form.startDate.trim(),
    endDate: form.endDate.trim(),
    period: form.period,
    content: form.content ?? '',
    attachedPdfUrl: (form.attachedPdfUrl || '').trim() || null,
    autoDiscountPct: form.autoDiscountPct === '' ? null : Number(form.autoDiscountPct),
    status: form.status,
    signedAt: (form.signedAt || '').trim() || null,
  });

  // andNew: kaydet ama listeye/detaya gitme — formu sıfırlayıp boş yeni kayda hazırlan.
  const persist = async ({ andNew = false } = {}) => {
    setTried(true);
    if (!form.customerId.trim()) { showToast('error', 'Müşteri zorunlu.'); return; }
    if (!form.title.trim()) { showToast('error', 'Başlık zorunlu.'); return; }
    if (!form.startDate.trim()) { showToast('error', 'Başlangıç tarihi zorunlu.'); return; }
    if (!form.endDate.trim()) { showToast('error', 'Bitiş tarihi zorunlu.'); return; }
    setSaving(true);
    try {
      const res = await fetch(isNew ? API_BASE : `${API_BASE}/${id}`, {
        method: isNew ? 'POST' : 'PUT', headers: authHeaders(), body: JSON.stringify(buildBody()),
      });
      const body = await res.json().catch(() => null);
      if (!res.ok) throw new Error(pick(body, 'Message', 'message') || `İşlem başarısız (${res.status})`);
      if (isNew) {
        if (andNew) {
          showToast('success', 'Anlaşma eklendi. Yeni kayda devam edebilirsiniz.');
          setForm(EMPTY_FORM);
          setTried(false);
          window.scrollTo({ top: 0, behavior: 'smooth' });
          return;
        }
        showToast('success', 'Anlaşma oluşturuldu.');
        setTimeout(() => navigate('/agreements'), 400);
        return;
      }
      showToast('success', 'Anlaşma güncellendi.');
      setTimeout(() => navigate('/agreements'), 400);
    } catch (err) {
      showToast('error', err.message || 'İşlem başarısız');
    } finally {
      setSaving(false);
    }
  };

  const handleSave = () => persist();

  // Klonla: mevcut kaydın tüm form bilgilerini taşıyarak yeni-kayıt ekranını aç. Hiçbir şey kaydedilmez.
  const handleClone = () => {
    navigate('/agreements/new', { state: { clone: { ...form } } });
  };

  const handleDelete = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/${id}`, { method: 'DELETE', headers: authHeaders() });
      const body = await res.json().catch(() => null);
      if (!res.ok) throw new Error(pick(body, 'Message', 'message') || `Silme başarısız (${res.status})`);
      showToast('success', 'Anlaşma silindi.');
      setTimeout(() => navigate('/agreements'), 400);
    } catch (err) {
      showToast('error', err.message || 'Silme başarısız');
      setSaving(false);
      setDeleteConfirm(false);
    }
  };

  const title = isNew ? 'Yeni Anlaşma' : (form.title || 'Anlaşma Detayı');
  const statusMeta = STATUS_META[form.status] || { label: form.status || '-', tone: 'neutral' };
  const statusBadge = !isNew && <StatusBadge tone={statusMeta.tone}>{statusMeta.label}</StatusBadge>;

  const actions = (
    <>
      {!isNew && canDelete && <button className="dps-btn danger" onClick={() => setDeleteConfirm(true)} disabled={saving}><Icon name="x" size={16} /> Sil</button>}
      <button className="dps-btn ghost" onClick={() => navigate('/agreements')} disabled={saving}>İptal</button>
      {canSave && (isNew ? (
        <SplitButton
          label={saving ? 'Kaydediliyor…' : 'Kaydet'}
          icon="check"
          onClick={handleSave}
          disabled={saving}
          menu={[{
            label: 'Kaydet ve Yeni',
            desc: 'Kaydet, listeye dönme, yeni kayıt aç',
            icon: 'plus',
            onClick: () => persist({ andNew: true }),
          }]}
        />
      ) : (
        <SplitButton
          label={saving ? 'Kaydediliyor…' : 'Kaydet'}
          icon="check"
          onClick={handleSave}
          disabled={saving}
          menu={[{
            label: 'Klonla',
            desc: 'Bu kaydı kopyalayıp yeni anlaşma aç',
            icon: 'copy',
            onClick: handleClone,
          }]}
        />
      ))}
    </>
  );

  return (
    <DetailShell
      crumb={[{ label: 'Anlaşmalar', to: '/agreements' }, { label: isNew ? 'Yeni Anlaşma' : 'Anlaşma Detayı', current: true }]}
      status={statusBadge}
      actions={actions}
      audit={isNew ? null : audit}
      history={isNew ? null : { entityName: 'Agreement', entityId: id }}
      loading={loading}
    >
      <div className="dps-form">
        <Section icon="briefcase" title="Anlaşma Bilgileri" subtitle="Müşteri, tip, dönem ve geçerlilik">
          <Grid cols={2}>
            <FieldCustom label="Müşteri" req invalid={tried && !String(form.customerId ?? '').trim()}>
              <RelationPicker
                config={RELATION_CONFIGS.customer}
                value={form.customerId || ''}
                selectedLabel={form.customerTitle || ''}
                onChange={(val, item) => setForm((c) => ({ ...c, customerId: val, customerTitle: item ? customerLookupLabel(item) : '' }))}
                placeholder="Müşteri kodu veya adı ile ara…"
              />
            </FieldCustom>
            <FieldSelect form={form} set={set} k="type" label="Tip" req options={TYPE_OPTIONS} />
            <FieldText form={form} set={set} tried={tried} k="title" label="Başlık" req span placeholder="Başlık giriniz" />
            <FieldDate form={form} set={set} tried={tried} k="startDate" label="Başlangıç Tarihi" req />
            <FieldDate form={form} set={set} tried={tried} k="endDate" label="Bitiş Tarihi" req />
            <FieldSelect form={form} set={set} k="period" label="Dönem" req options={PERIOD_OPTIONS} />
            <FieldSelect form={form} set={set} k="status" label="Durum" req options={STATUS_OPTIONS} />
            <FieldText form={form} set={set} k="autoDiscountPct" label="Oto. İndirim %" num type="number" placeholder="(opsiyonel)" />
            <FieldDate form={form} set={set} k="signedAt" label="İmza Tarihi" />
          </Grid>
        </Section>

        <Section icon="list" title="İçerik & Ek" subtitle="Sözleşme metni ve ekli PDF">
          <Grid cols={2}>
            <FieldText form={form} set={set} k="attachedPdfUrl" label="Ek PDF URL" span placeholder="https://..." />
            <FieldTextarea form={form} set={set} k="content" label="Sözleşme İçeriği" rows={8} span />
          </Grid>
        </Section>
      </div>

      {/* Silme onayı */}
      {deleteConfirm && (
        <SubModal
          title="Silme Onayı"
          maxWidth={440}
          onClose={() => setDeleteConfirm(false)}
          footer={(
            <>
              <button className="dps-btn ghost" onClick={() => setDeleteConfirm(false)} disabled={saving}>İptal</button>
              <button className="dps-btn danger" onClick={handleDelete} disabled={saving}>{saving ? 'Siliniyor…' : 'Evet, Sil'}</button>
            </>
          )}
        >
          <p style={{ margin: 0, lineHeight: 1.6 }}>
            <strong>“{form.title}”</strong> anlaşmasını silmek istediğinize emin misiniz?
          </p>
          <p style={{ fontSize: 12, color: '#dc2626', marginTop: 8 }}>Mobile bu anlaşmayı bir sonraki sync&apos;te artık göstermeyecek.</p>
        </SubModal>
      )}

      <Toast toast={toast} />
    </DetailShell>
  );
}

export default AgreementDetailPage;
